import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, UserPlus, Link2, Building2, CheckCircle2, ArrowRight, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import DuplicateConfirmDialog from "@/components/DuplicateConfirmDialog";

const ContactForm = ({ extractedData, existingContact, existingCompany, apiUrl, createdContact, createdCompany, onContactCreated, onCompanyCreated }) => {
  const [loading, setLoading] = useState(false);
  // step 1=form, 2=creating company, 3=creating contact, 4=done. Restored to 4 if persisted.
  const [step, setStep] = useState(createdContact ? 4 : 1);
  const [forceNewContact, setForceNewContact] = useState(false);
  const [createCompanyFirst, setCreateCompanyFirst] = useState(
    (!existingCompany || !existingCompany.sellsy_id) && !!extractedData?.company
  );
  // Duplicate dialog state (shared by company + contact creation)
  const [dupOpen, setDupOpen] = useState(false);
  const [dupInfo, setDupInfo] = useState(null);
  const [dupResolver, setDupResolver] = useState(null);

  // Resolver receives one of:
  //   - true              → force retry
  //   - false             → cancel
  //   - { useExisting: m} → user picked an existing Sellsy match
  const askDuplicate = (info) => new Promise((resolve) => {
    setDupInfo(info);
    setDupOpen(true);
    setDupResolver(() => resolve);
  });

  const postWithDupCheck = async (url, payload) => {
    try {
      const res = await axios.post(url, payload);
      return { data: res.data, existing: false };
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "duplicate") {
        const choice = await askDuplicate(detail);
        if (!choice) return { data: null, existing: false };
        if (choice && typeof choice === "object" && choice.useExisting) {
          // Build a normalized record from the Sellsy match
          const m = choice.useExisting;
          return {
            data: {
              id: m.sellsy_id,
              sellsy_id: m.sellsy_id,
              name: m.name,
              email: m.email,
              ...payload,
            },
            existing: true,
          };
        }
        const sep = url.includes("?") ? "&" : "?";
        const res2 = await axios.post(`${url}${sep}force=true`, payload);
        return { data: res2.data, existing: false };
      }
      throw err;
    }
  };
  
  const [formData, setFormData] = useState({
    first_name: extractedData?.first_name || "",
    last_name: extractedData?.last_name || "",
    email: extractedData?.email || "",
    job_title: extractedData?.job_title || "",
    company: extractedData?.company || "",
    mobile_phone: extractedData?.mobile_phone || "",
    landline_phone: extractedData?.landline_phone || "",
    address: extractedData?.address || "",
    postal_code: extractedData?.postal_code || "",
    city: extractedData?.city || "",
    client_activity: extractedData?.client_activity || ""
  });

  // Sync form when extractedData changes (e.g. user edits in Apercu)
  useEffect(() => {
    if (!extractedData) return;
    setFormData({
      first_name: extractedData.first_name || "",
      last_name: extractedData.last_name || "",
      email: extractedData.email || "",
      job_title: extractedData.job_title || "",
      company: extractedData.company || "",
      mobile_phone: extractedData.mobile_phone || "",
      landline_phone: extractedData.landline_phone || "",
      address: extractedData.address || "",
      postal_code: extractedData.postal_code || "",
      city: extractedData.city || "",
      client_activity: extractedData.client_activity || ""
    });
    setCreateCompanyFirst((!existingCompany || !existingCompany.sellsy_id) && !!extractedData.company);
  }, [extractedData, existingCompany]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error("L'email est obligatoire");
      return;
    }

    setLoading(true);
    
    try {
      let companyId = (existingCompany?.sellsy_id) ? (existingCompany.sellsy_id || existingCompany.id) : null;
      
      // Étape 1: Créer la société si nécessaire (pas de sellsy_id = pas dans Sellsy)
      if (createCompanyFirst && formData.company && (!existingCompany || !existingCompany.sellsy_id)) {
        setStep(2);
        toast.info("Création de la société dans Sellsy...");
        
        const result = await postWithDupCheck(`${apiUrl}/companies`, {
          name: formData.company,
          address: formData.address || null,
          postal_code: formData.postal_code || null,
          city: formData.city || null,
          client_activity: formData.client_activity || null
        });
        if (!result.data) {
          toast.info("Création annulée");
          setStep(1);
          setLoading(false);
          return;
        }
        companyId = result.data.sellsy_id || result.data.id;
        if (onCompanyCreated) onCompanyCreated(result.data);
        toast.success(
          result.existing
            ? `Société existante "${result.data.name}" sélectionnée`
            : `Société "${formData.company}" créée avec succès`
        );
      }

      // Étape 1bis: si on a une companyId Sellsy et une adresse extraite, on
      // s'assure que l'adresse est présente côté Sellsy (idempotent : ne crée
      // rien si la société a déjà au moins une adresse). Best-effort, ne bloque
      // jamais la création du contact si ça échoue.
      const hasAddrData = !!(formData.address || formData.postal_code || formData.city);
      if (companyId && hasAddrData) {
        try {
          const addrRes = await fetch(`${apiUrl}/companies/${companyId}/ensure-address`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              address: formData.address || null,
              postal_code: formData.postal_code || null,
              city: formData.city || null,
            }),
          });
          if (addrRes.ok) {
            const addrJson = await addrRes.json();
            if (addrJson.status === "created") {
              toast.success("Adresse ajoutée à la société");
            }
            // 'already_present' or 'skipped' → silent (no need to bother the user)
          }
        } catch (_) {
          // Silent: address sync is best-effort
        }
      }
      
      // Étape 2: Créer le contact rattaché à la société
      setStep(3);
      toast.info("Création du contact dans Sellsy...");
      
      const contactPayload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        job_title: formData.job_title || null,
        company: formData.company,
        mobile_phone: formData.mobile_phone,
        landline_phone: formData.landline_phone,
        sellsy_company_id: companyId,
        client_activity: formData.client_activity || null
      };
      
      const cResult = await postWithDupCheck(`${apiUrl}/contacts`, contactPayload);
      if (!cResult.data) {
        toast.info("Création annulée");
        setStep(1);
        setLoading(false);
        return;
      }
      if (onContactCreated) onContactCreated(cResult.data);
      
      setStep(4);
      toast.success(
        cResult.existing
          ? "Contact existant sélectionné, rattachement OK"
          : "Contact créé et rattaché avec succès !"
      );
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.message || "Erreur lors de la création";
      toast.error(message);
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  // Restore step=4 if a contact has been created (persistence across tab switches)
  useEffect(() => {
    if (createdContact && step !== 4) setStep(4);
    if (!createdContact && step === 4) setStep(1);
  }, [createdContact, step]);

  // Si contact déjà existant DANS SELLSY, afficher l'option de liaison (sauf si on force la création)
  if (existingContact && existingContact.sellsy_id && !forceNewContact && !createdContact) {
    return (
      <div className="space-y-3" data-testid="existing-contact-view">
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-emerald-700 mb-1">
                Contact existant dans Sellsy
              </p>
              <p className="text-sm text-emerald-900">{existingContact.name}</p>
              <p className="text-xs text-emerald-600">{existingContact.email}</p>
              {existingContact.company && (
                <p className="text-xs text-emerald-600 mt-1">
                  <Building2 className="w-3 h-3 inline mr-1" />
                  {existingContact.company}
                </p>
              )}
            </div>
            <a
              href={`https://www.sellsy.com/peoples/${existingContact.sellsy_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-600 hover:text-emerald-800 flex-shrink-0 mt-0.5"
              title="Ouvrir dans Sellsy"
              data-testid="sellsy-existing-contact-link"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
        
        <Button
          variant="secondary"
          className="w-full h-8 text-xs"
          data-testid="link-existing-contact-btn"
        >
          <Link2 className="w-3.5 h-3.5 mr-1.5" />
          Utiliser ce contact existant
        </Button>
        
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center text-xs">
            <button 
              onClick={() => setForceNewContact(true)}
              className="bg-white px-2 text-slate-500 hover:text-slate-700 cursor-pointer"
              data-testid="force-new-contact-btn"
            >
              ou créer un nouveau contact
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Afficher le résultat de création (persiste tant qu'aucun nouvel email n'est analysé)
  if (step === 4 && createdContact) {
    return (
      <div className="space-y-3" data-testid="creation-success">
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <p className="text-sm font-medium text-emerald-900">Création réussie !</p>
        </div>
        
        {createdCompany && (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 text-slate-600" />
              <span className="text-xs font-medium text-slate-700">Société créée</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-slate-900 truncate">{createdCompany.name}</p>
              {createdCompany.sellsy_id && (
                <a
                  href={`https://www.sellsy.com/companies/${createdCompany.sellsy_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-slate-700 flex-shrink-0"
                  title="Ouvrir dans Sellsy"
                  data-testid="sellsy-company-link"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
            {createdCompany.sellsy_id && (
              <p className="text-xs text-slate-500">ID Sellsy: {createdCompany.sellsy_id}</p>
            )}
          </div>
        )}
        
        <div className="flex items-center justify-center text-slate-400">
          <ArrowRight className="w-4 h-4" />
        </div>
        
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="w-4 h-4 text-slate-600" />
            <span className="text-xs font-medium text-slate-700">Contact créé</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-slate-900 truncate">
              {createdContact.first_name} {createdContact.last_name}
            </p>
            {createdContact.sellsy_id && (
              <a
                href={`https://www.sellsy.com/peoples/${createdContact.sellsy_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-slate-700 flex-shrink-0"
                title="Ouvrir dans Sellsy"
                data-testid="sellsy-contact-link"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
          <p className="text-xs text-slate-600">{createdContact.email}</p>
          {createdContact.sellsy_id && (
            <p className="text-xs text-slate-500">ID Sellsy: {createdContact.sellsy_id}</p>
          )}
          {createdCompany && (
            <p className="text-xs text-emerald-600 mt-1">
              <Link2 className="w-3 h-3 inline mr-1" />
              Rattaché à {createdCompany.name}
            </p>
          )}
        </div>
        
        <Button
          variant="secondary"
          onClick={() => {
            setStep(1);
            if (onContactCreated) onContactCreated(null);
            if (onCompanyCreated) onCompanyCreated(null);
          }}
          className="w-full h-8 text-xs"
          data-testid="create-another-btn"
        >
          Créer un autre contact
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="contact-form">
      {/* Indicateur de flux si société à créer */}
      {formData.company && (!existingCompany || !existingCompany.sellsy_id) && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="create-company-first"
              checked={createCompanyFirst}
              onCheckedChange={setCreateCompanyFirst}
              data-testid="create-company-checkbox"
            />
            <label 
              htmlFor="create-company-first" 
              className="text-xs text-blue-700 cursor-pointer"
            >
              Créer d'abord la société <strong>"{formData.company}"</strong> dans Sellsy, puis rattacher le contact
            </label>
          </div>
        </div>
      )}

      {/* Progress indicator during creation */}
      {loading && step > 1 && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
          <div className="flex items-center gap-3">
            <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-xs">
                <span className={step >= 2 ? "text-slate-900 font-medium" : "text-slate-400"}>
                  1. Société
                </span>
                <ArrowRight className="w-3 h-3 text-slate-300" />
                <span className={step >= 3 ? "text-slate-900 font-medium" : "text-slate-400"}>
                  2. Contact
                </span>
                <ArrowRight className="w-3 h-3 text-slate-300" />
                <span className={step >= 4 ? "text-slate-900 font-medium" : "text-slate-400"}>
                  3. Terminé
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {step === 2 && "Création de la société dans Sellsy..."}
                {step === 3 && "Création du contact et rattachement..."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Société existante détectée dans Sellsy */}
      {existingCompany && existingCompany.sellsy_id && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <div>
              <p className="text-xs font-medium text-emerald-700">Société existante dans Sellsy</p>
              <p className="text-xs text-emerald-600">{existingCompany.name}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-slate-600">Prénom</Label>
          <Input
            type="text"
            value={formData.first_name}
            onChange={(e) => handleChange("first_name", e.target.value)}
            className="h-8 text-sm"
            placeholder="Prénom"
            disabled={loading}
            data-testid="contact-first-name-input"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Nom</Label>
          <Input
            type="text"
            value={formData.last_name}
            onChange={(e) => handleChange("last_name", e.target.value)}
            className="h-8 text-sm"
            placeholder="Nom"
            disabled={loading}
            data-testid="contact-last-name-input"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs text-slate-600">Email *</Label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          className="h-8 text-sm"
          placeholder="email@exemple.com"
          required
          disabled={loading}
          data-testid="contact-email-input"
        />
      </div>

      <div>
        <Label className="text-xs text-slate-600">Fonction</Label>
        <Input
          type="text"
          value={formData.job_title}
          onChange={(e) => handleChange("job_title", e.target.value)}
          className="h-8 text-sm"
          placeholder="Ex: Assistante de direction"
          disabled={loading}
          data-testid="contact-job-title-input"
        />
      </div>

      <div>
        <Label className="text-xs text-slate-600">Société</Label>
        <Input
          type="text"
          value={formData.company}
          onChange={(e) => handleChange("company", e.target.value)}
          className="h-8 text-sm"
          placeholder="Nom de la société"
          disabled={loading}
          data-testid="contact-company-input"
        />
      </div>

      <div>
        <Label className="text-xs text-slate-600">Adresse</Label>
        <Input
          type="text"
          value={formData.address}
          onChange={(e) => handleChange("address", e.target.value)}
          className="h-8 text-sm"
          placeholder="N° et nom de rue"
          disabled={loading}
          data-testid="contact-address-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-slate-600">Code postal</Label>
          <Input
            type="text"
            value={formData.postal_code}
            onChange={(e) => handleChange("postal_code", e.target.value)}
            className="h-8 text-sm"
            placeholder="75001"
            disabled={loading}
            data-testid="contact-postal-code-input"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Ville</Label>
          <Input
            type="text"
            value={formData.city}
            onChange={(e) => handleChange("city", e.target.value)}
            className="h-8 text-sm"
            placeholder="Paris"
            disabled={loading}
            data-testid="contact-city-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-slate-600">Portable</Label>
          <Input
            type="tel"
            value={formData.mobile_phone}
            onChange={(e) => handleChange("mobile_phone", e.target.value)}
            className="h-8 text-sm"
            placeholder="06..."
            disabled={loading}
            data-testid="contact-mobile-input"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Fixe</Label>
          <Input
            type="tel"
            value={formData.landline_phone}
            onChange={(e) => handleChange("landline_phone", e.target.value)}
            className="h-8 text-sm"
            placeholder="01..."
            disabled={loading}
            data-testid="contact-landline-input"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs text-slate-600">Activité client</Label>
        <Input
          type="text"
          value={formData.client_activity}
          onChange={(e) => handleChange("client_activity", e.target.value)}
          className="h-8 text-sm"
          placeholder="Ex: Entreprise, Hotel, Mairie..."
          data-testid="contact-activity-input"
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-8 text-xs bg-slate-900 hover:bg-slate-800"
        data-testid="create-contact-submit-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            {step === 2 ? "Création société..." : step === 3 ? "Création contact..." : "Création..."}
          </>
        ) : (
          <>
            {createCompanyFirst && formData.company && !existingCompany ? (
              <>
                <Building2 className="w-3.5 h-3.5 mr-1.5" />
                Créer société + contact
              </>
            ) : (
              <>
                <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                Créer le contact
              </>
            )}
          </>
        )}
      </Button>
      <DuplicateConfirmDialog
        open={dupOpen}
        onOpenChange={(next) => {
          setDupOpen(next);
          if (!next && dupResolver) {
            dupResolver(false);
            setDupResolver(null);
          }
        }}
        info={dupInfo}
        onConfirm={() => {
          if (dupResolver) {
            dupResolver(true);
            setDupResolver(null);
          }
        }}
        onUseExisting={(match) => {
          if (dupResolver) {
            dupResolver({ useExisting: match });
            setDupResolver(null);
          }
        }}
      />
    </form>
  );
};

export default ContactForm;
